import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { InterviewQuestion, InterviewAnswer, WordAnalysis, QuestionCount } from "../../types";
import {
  generateInterviewQuestions,
  transcribeAudio,
  evaluateAnswer,
  generateFollowUpQuestion,
  synthesizeSpeech,
  TTSVoice,
} from "../../services/api";
import { calculateLevenshteinDistance, getMatchQuality } from "../../utils/levenshtein";
import useAudioRecorder from "../../hooks/useAudioRecorder";
import useSounds from "../../hooks/useSounds";
import RecordButton from "../RecordButton";
import LoadingState from "../LoadingState";
import ErrorState from "../ErrorState";
import SilenceDetectionModal from "../SilenceDetectionModal";
import {
  fallbackInterviewQuestions,
  fallbackEvaluation,
  fallbackMessages
} from "../../config/fallbackQuestions";

// Minimum RMS threshold for silence detection
const SILENCE_THRESHOLD = 0.01;

// TTS voice for interview questions (American casual)
const INTERVIEW_VOICE: TTSVoice = 'en-US-GuyNeural';

interface InterviewQuestionsProps {
  jobTitle: string;
  roleDescription?: string;
  questionCount: QuestionCount;
  questions: InterviewQuestion[];
  currentQuestionIndex: number;
  onQuestionsGenerated: (questions: string[]) => void;
  onAnswerSubmitted: (questionIndex: number, answer: InterviewAnswer) => void;
  hellMode?: boolean;
}

// Analyze audio for silence
const analyzeAudioForSilence = async (blob: Blob): Promise<boolean> => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.getChannelData(0);

    let sumSquares = 0;
    for (let i = 0; i < channelData.length; i++) {
      sumSquares += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSquares / channelData.length);
    await audioContext.close();

    return rms > SILENCE_THRESHOLD;
  } catch (err) {
    console.error('Error analyzing audio:', err);
    return true;
  }
};

const InterviewQuestions: React.FC<InterviewQuestionsProps> = ({
  jobTitle,
  roleDescription,
  questionCount,
  questions,
  currentQuestionIndex,
  onQuestionsGenerated,
  onAnswerSubmitted,
  hellMode,
}) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [showSilenceModal, setShowSilenceModal] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{ question: string; answer: string }>>([]);

  // TTS state
  const [questionAudioUrl, setQuestionAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isPlayingQuestion, setIsPlayingQuestion] = useState(false);
  const [ttsAvailable, setTtsAvailable] = useState(true);
  const [showQuestionText, setShowQuestionText] = useState(false);
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);

  // Loading next question
  const [isLoadingNextQuestion, setIsLoadingNextQuestion] = useState(false);

  const processedBlobRef = useRef<Blob | null>(null);
  const questionGenerationStartedRef = useRef(false);
  const audioPreloadingRef = useRef(false);

  const {
    isRecording,
    audioBlob,
    error: recordingError,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecorder();
  const { playSound } = useSounds();

  // Generate initial question when component mounts
  useEffect(() => {
    if (questions.length === 0 && !isGenerating && !questionGenerationStartedRef.current) {
      questionGenerationStartedRef.current = true;
      generateFirstQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track if audio has been played for current question
  const [hasPlayedAudio, setHasPlayedAudio] = useState(false);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);

  // Auto-load audio when question changes
  useEffect(() => {
    if (questions.length > 0 && questions[currentQuestionIndex]) {
      // Reset state
      if (questionAudioUrl) {
        URL.revokeObjectURL(questionAudioUrl);
      }
      setQuestionAudioUrl(null);
      setIsPlayingQuestion(false);
      setTtsAvailable(true);
      setShowQuestionText(false);
      setHasPlayedAudio(false);
      setAutoPlayBlocked(false);
      audioPreloadingRef.current = false; // Reset for new question

      // Auto-load and auto-play the audio
      preloadQuestionAudio(questions[currentQuestionIndex].question);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, questions.length]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
      }
      if (questionAudioUrl) {
        URL.revokeObjectURL(questionAudioUrl);
      }
    };
  }, [questionAudioUrl]);

  // Load and auto-play TTS audio for a question
  const preloadQuestionAudio = async (questionText: string) => {
    if (audioPreloadingRef.current || isLoadingAudio) return;
    audioPreloadingRef.current = true;
    setIsLoadingAudio(true);
    setHasPlayedAudio(false);

    try {
      const audioBlob = await synthesizeSpeech(questionText, INTERVIEW_VOICE);
      const url = URL.createObjectURL(audioBlob);
      setQuestionAudioUrl(url);
      setTtsAvailable(true);

      // Auto-play the audio once loaded
      const audio = new Audio(url);
      questionAudioRef.current = audio;

      audio.onended = () => {
        setIsPlayingQuestion(false);
        setHasPlayedAudio(true);
      };
      audio.onerror = () => {
        setIsPlayingQuestion(false);
        setHasPlayedAudio(true);
      };

      try {
        await audio.play();
        setIsPlayingQuestion(true);
      } catch (playErr) {
        console.error('Auto-play blocked by browser:', playErr);
        setAutoPlayBlocked(true); // Show play button when auto-play is blocked
      }
    } catch (err) {
      console.error('TTS preload error:', err);
      setTtsAvailable(false);
    } finally {
      setIsLoadingAudio(false);
      audioPreloadingRef.current = false;
    }
  };

  // Play the preloaded question audio
  const playQuestionAudio = async () => {
    if (!questionAudioUrl) return;

    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
    }

    const audio = new Audio(questionAudioUrl);
    questionAudioRef.current = audio;

    audio.onended = () => {
      setIsPlayingQuestion(false);
      setHasPlayedAudio(true);
      setAutoPlayBlocked(false);
    };
    audio.onerror = () => {
      setIsPlayingQuestion(false);
    };

    try {
      await audio.play();
      setIsPlayingQuestion(true);
    } catch (err) {
      console.error('Audio play error:', err);
    }
  };

  // Play/stop question audio
  const toggleQuestionAudio = async () => {
    if (isPlayingQuestion && questionAudioRef.current) {
      questionAudioRef.current.pause();
      setIsPlayingQuestion(false);
    } else if (questionAudioUrl) {
      await playQuestionAudio();
    }
  };

  // Process audio when recording is complete
  useEffect(() => {
    const processAudio = async () => {
      if (audioBlob && audioBlob !== processedBlobRef.current && questions.length > 0) {
        // First check for silence
        const hasSound = await analyzeAudioForSilence(audioBlob);
        if (!hasSound) {
          setShowSilenceModal(true);
          return;
        }

        processedBlobRef.current = audioBlob;
        setIsProcessing(true);
        setProcessingStep(0);
        setError(null);

        try {
          // Step 1: Transcribe the audio
          setProcessingStep(1);
          const transcription = await transcribeAudio(audioBlob);

          // Step 2: Analyze pronunciation
          setProcessingStep(2);
          const targetWords = transcription.text.split(/\s+/).filter(w => w.length > 0);
          const levenshteinResult = calculateLevenshteinDistance(targetWords, transcription.words);

          const wordAnalysis: WordAnalysis[] = levenshteinResult.diffs.map(diff => {
            if (diff.operation === 'match') {
              const quality = getMatchQuality(diff.confidence);
              return {
                word: diff.targetWord!,
                status: quality,
                confidence: diff.confidence,
                transcribed: diff.transcribedWord!,
              };
            }
            if (diff.operation === 'substitution') {
              return {
                word: diff.targetWord!,
                status: 'substitution',
                confidence: diff.confidence,
                transcribed: diff.transcribedWord!,
              };
            }
            if (diff.operation === 'omission') {
              return {
                word: diff.targetWord!,
                status: 'omission',
                confidence: 0,
              };
            }
            return {
              word: diff.transcribedWord!,
              status: 'insertion',
              confidence: diff.confidence,
              transcribed: diff.transcribedWord!,
            };
          });

          const correctWords = wordAnalysis.filter(w => w.status === 'correct').length;
          const totalTargetWords = targetWords.length || 1;
          const pronunciationScore = Math.round((correctWords / totalTargetWords) * 100);

          // Step 3: Evaluate the answer quality
          setProcessingStep(3);
          const currentQuestion = questions[currentQuestionIndex];

          let evaluation;
          try {
            const evaluationResponse = await evaluateAnswer({
              jobTitle,
              question: currentQuestion.question,
              answer: transcription.text,
            });
            evaluation = evaluationResponse.evaluation;
          } catch (evalErr) {
            console.error("Evaluation error:", evalErr);
            evaluation = fallbackEvaluation;
            setFallbackMessage(fallbackMessages.answerEvaluation);
          }

          const answer: InterviewAnswer = {
            transcription: transcription.text,
            pronunciationScore,
            wordAnalysis,
            evaluation,
          };

          // Update conversation history
          const newHistory = [
            ...conversationHistory,
            { question: currentQuestion.question, answer: transcription.text }
          ];
          setConversationHistory(newHistory);

          // Generate follow-up question if not the last question
          if (currentQuestionIndex < questionCount - 1) {
            setProcessingStep(4);
            setIsLoadingNextQuestion(true);

            try {
              const followUp = await generateFollowUpQuestion({
                jobTitle,
                previousQuestion: currentQuestion.question,
                previousAnswer: transcription.text,
                questionNumber: currentQuestionIndex + 2,
                totalQuestions: questionCount,
                conversationHistory: newHistory,
                hellMode,
              });

              // Add the follow-up question to the questions array
              onQuestionsGenerated([
                ...questions.map(q => q.question),
                followUp.question
              ].slice(0, currentQuestionIndex + 2));
            } catch (followUpErr) {
              console.error("Follow-up generation error:", followUpErr);
              // Fall back to a generic follow-up
              const genericFollowUp = `Can you tell me more about your experience related to ${jobTitle}?`;
              onQuestionsGenerated([
                ...questions.map(q => q.question),
                genericFollowUp
              ].slice(0, currentQuestionIndex + 2));
            } finally {
              setIsLoadingNextQuestion(false);
            }
          }

          playSound('complete');
          onAnswerSubmitted(currentQuestionIndex, answer);
          resetRecording();
          processedBlobRef.current = null;
        } catch (err) {
          console.error("Processing error:", err);
          setError(fallbackMessages.transcription);
          playSound('error');
        } finally {
          setIsProcessing(false);
          setProcessingStep(0);
        }
      }
    };

    processAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob]);

  const getProcessingSteps = (): { label: string; status: 'pending' | 'active' | 'completed' }[] => {
    const getStatus = (completedAt: number, activeAt: number): 'pending' | 'active' | 'completed' => {
      if (processingStep >= completedAt) return 'completed';
      if (processingStep === activeAt) return 'active';
      return 'pending';
    };

    const steps = [
      { label: 'Uploading audio', status: processingStep >= 1 ? 'completed' : 'active' },
      { label: 'Transcribing speech', status: getStatus(2, 1) },
      { label: 'Analyzing pronunciation', status: getStatus(3, 2) },
      { label: 'Evaluating answer quality', status: getStatus(4, 3) },
    ];

    if (currentQuestionIndex < questionCount - 1) {
      steps.push({ label: 'Preparing next question', status: processingStep === 4 ? 'active' : 'pending' });
    }

    return steps as { label: string; status: 'pending' | 'active' | 'completed' }[];
  };

  const generateFirstQuestion = async () => {
    setIsGenerating(true);
    setError(null);
    setFallbackMessage(null);

    try {
      const response = await generateInterviewQuestions({
        jobTitle,
        roleDescription,
        questionCount: 1, // Only generate first question
        hellMode,
      });
      onQuestionsGenerated(response.questions.slice(0, 1));
    } catch (err) {
      console.error("Question generation error:", err);
      setFallbackMessage(fallbackMessages.questionsGeneration);
      onQuestionsGenerated([fallbackInterviewQuestions[0]]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSilenceModalClose = () => {
    setShowSilenceModal(false);
    processedBlobRef.current = null;
    resetRecording();
  };

  const handleSilenceModalTryAgain = () => {
    setShowSilenceModal(false);
    processedBlobRef.current = null;
    resetRecording();
    setTimeout(() => startRecording(), 100);
  };

  if (error && questions.length === 0) {
    return (
      <ErrorState
        title="Failed to Generate Questions"
        message={error}
        icon="network"
        onRetry={generateFirstQuestion}
        actions={[
          {
            label: 'Go to Home',
            onClick: () => navigate('/'),
            variant: 'secondary',
          },
        ]}
      />
    );
  }

  if (isGenerating || questions.length === 0) {
    return (
      <LoadingState
        title="Preparing Your Interview"
        message={`Getting ready for: ${jobTitle}`}
        steps={[
          { label: 'Analyzing job requirements', status: 'completed' },
          { label: 'Preparing first question', status: 'active' },
        ]}
        variant="spinner"
        estimateText="This usually takes 3-5 seconds..."
        darkMode={hellMode}
      />
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const displayError = error || recordingError;

  return (
    <div>
      {/* Hell Mode badge */}
      {hellMode && (
        <div className="mb-4 flex items-center justify-center">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 animate-pulse">
            <span>🔥</span>
            NIGHTMARE MODE ACTIVE
            <span>🔥</span>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm ${hellMode ? 'text-red-400' : 'text-gray-600'}`}>
            Question {currentQuestionIndex + 1} of {questionCount}
          </span>
          <span className={`text-sm font-medium ${hellMode ? 'text-red-500' : 'text-purple-600'}`}>{jobTitle}</span>
        </div>
        <div className={`w-full rounded-full h-2 ${hellMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              hellMode ? 'bg-gradient-to-r from-red-600 to-orange-500' : 'bg-purple-600'
            }`}
            style={{
              width: `${((currentQuestionIndex + 1) / questionCount) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Fallback message banner */}
      {fallbackMessage && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-amber-700 text-sm">{fallbackMessage}</p>
          </div>
        </div>
      )}

      {/* Question card with audio player */}
      <div className={`rounded-xl shadow-md p-6 mb-6 ${
        hellMode
          ? 'bg-gray-900 border border-red-800'
          : 'bg-white'
      }`}>
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            hellMode
              ? 'bg-gradient-to-br from-red-600 to-orange-600'
              : 'bg-purple-100'
          }`}>
            <span className={`font-bold ${hellMode ? 'text-white' : 'text-purple-600'}`}>{currentQuestionIndex + 1}</span>
          </div>
          <div className="flex-1">
            {/* Play Question button - always visible */}
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={toggleQuestionAudio}
                disabled={isLoadingAudio || !questionAudioUrl}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg text-base font-medium transition-all ${
                  isLoadingAudio || !questionAudioUrl
                    ? hellMode ? 'bg-gray-800 text-gray-500 cursor-wait' : 'bg-gray-100 text-gray-500 cursor-wait'
                    : isPlayingQuestion
                    ? hellMode ? 'bg-red-900 text-red-300 border border-red-600' : 'bg-purple-100 text-purple-700 border border-purple-300'
                    : hellMode ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-700 hover:to-orange-700 shadow-md shadow-red-900/50' : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md'
                }`}
              >
                {isLoadingAudio ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </>
                ) : isPlayingQuestion ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    Stop
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {hasPlayedAudio ? 'Play Again' : 'Play Question'}
                  </>
                )}
              </button>

              {/* Show/Hide text button */}
              <button
                onClick={() => setShowQuestionText(!showQuestionText)}
                className={`text-sm underline ${hellMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {showQuestionText ? 'Hide text' : 'Show text'}
              </button>
            </div>

            {/* Question text - shown when TTS fails OR when user clicks "Show text" */}
            {(showQuestionText || !ttsAvailable) && (
              <p className={`text-lg font-medium p-3 rounded-lg ${hellMode ? 'text-gray-100 bg-gray-800' : 'text-gray-800 bg-gray-50'}`}>
                {currentQuestion.question}
              </p>
            )}

            {/* Hint text */}
            {!showQuestionText && ttsAvailable && !isLoadingAudio && (
              <p className={`text-sm ${hellMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {isPlayingQuestion
                  ? 'Listen carefully...'
                  : autoPlayBlocked
                  ? 'Click "Play Question" to hear the interviewer'
                  : hasPlayedAudio
                  ? 'Click "Play Again" to hear the question again'
                  : 'Question will play automatically...'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {displayError && (
        <div className="mb-6">
          <ErrorState
            title="Answer Processing Failed"
            message={displayError}
            icon="error"
            showHomeButton={false}
            showRetryButton={true}
            onRetry={() => {
              setError(null);
              processedBlobRef.current = null;
              resetRecording();
            }}
          />
        </div>
      )}

      {/* Recording controls */}
      <div className="text-center">
        {isProcessing || isLoadingNextQuestion ? (
          <div className="py-4">
            <LoadingState
              title={isLoadingNextQuestion ? "Preparing Next Question" : "Processing Your Answer"}
              message={isLoadingNextQuestion ? "The interviewer is thinking..." : "Analyzing your response..."}
              steps={getProcessingSteps()}
              variant="spinner"
              estimateText={isLoadingNextQuestion ? "Almost ready..." : "This may take 10-15 seconds..."}
              darkMode={hellMode}
            />
          </div>
        ) : !displayError && (
          <>
            <div className="mb-4">
              <RecordButton
                isRecording={isRecording}
                isProcessing={isProcessing}
                recordingTime={recordingTime}
                onStart={startRecording}
                onStop={stopRecording}
                disabled={isLoadingAudio || isPlayingQuestion}
              />
            </div>
            <p className={`text-sm ${hellMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {isRecording
                ? "Recording... Click to stop when finished"
                : isPlayingQuestion
                ? "Listen to the question, then record your answer"
                : "Click the microphone to record your answer"}
            </p>
          </>
        )}
      </div>

      {/* Tips */}
      <div className={`mt-8 rounded-lg p-4 ${hellMode ? 'bg-gray-900 border border-red-900' : 'bg-purple-50'}`}>
        <h4 className={`font-medium mb-2 ${hellMode ? 'text-red-400' : 'text-purple-800'}`}>Tips for a great answer:</h4>
        <ul className={`text-sm space-y-1 ${hellMode ? 'text-gray-300' : 'text-purple-700'}`}>
          <li>- Speak clearly and at a natural pace</li>
          <li>- Use specific examples from your experience</li>
          <li>- Structure your answer: Situation, Task, Action, Result (STAR)</li>
          <li>- Keep your answer between 1-2 minutes</li>
        </ul>
      </div>

      {/* Silence Detection Modal */}
      <SilenceDetectionModal
        isOpen={showSilenceModal}
        onClose={handleSilenceModalClose}
        onTryAgain={handleSilenceModalTryAgain}
      />
    </div>
  );
};

export default InterviewQuestions;
